import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, RefreshCw, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getInwardEntries } from '@/services/api';
import { offlineStorage } from '@/services/offlineStorage';
import { toast } from 'sonner';

const MyEntries = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [unsyncedEntries, setUnsyncedEntries] = useState([]);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      // Get synced entries from server
      const serverData = await getInwardEntries(0, 1000);
      const serverEntries = serverData.entries || [];
      
      // Get unsynced entries from local storage
      const localEntries = await offlineStorage.getUnsyncedEntries();
      
      // Combine and sort by timestamp
      const allEntries = [...serverEntries, ...localEntries].sort((a, b) => {
        const dateA = new Date(a.timestamp || a.created_at);
        const dateB = new Date(b.timestamp || b.created_at);
        return dateB - dateA;
      });
      
      setEntries(allEntries);
      setFilteredEntries(allEntries);
      setUnsyncedEntries(localEntries);
    } catch (error) {
      console.error('Error fetching entries:', error);
      // Fallback to local storage only
      try {
        const localEntries = await offlineStorage.getInwardEntries(1000);
        setEntries(localEntries);
        setFilteredEntries(localEntries);
        setUnsyncedEntries(localEntries.filter(e => !e.synced));
      } catch (localError) {
        toast.error('Failed to load entries');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = () => {
    if (!fromDate && !toDate) {
      setFilteredEntries(entries);
      toast.success(`Showing ${entries.length} entries`);
      return;
    }

    const filtered = entries.filter(entry => {
      const timestampStr = entry.timestamp || entry.created_at;
      if (!timestampStr) return false;
      
      // Parse the timestamp to get just the date part (YYYY-MM-DD)
      // Handles both ISO format (2026-01-10T16:41:00+05:30) and other formats
      let datePart;
      if (timestampStr.includes('T')) {
        datePart = timestampStr.split('T')[0];
      } else if (timestampStr.includes(' ')) {
        // Handle "DD/MM/YYYY, HH:MM" format from display
        const dateMatch = timestampStr.match(/(\d{2})\/(\d{2})\/(\d{4})/);
        if (dateMatch) {
          datePart = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
        }
      } else {
        datePart = timestampStr.substring(0, 10);
      }
      
      if (!datePart) return false;
      
      // Compare dates as strings (YYYY-MM-DD format allows string comparison)
      if (fromDate && datePart < fromDate) return false;
      if (toDate && datePart > toDate) return false;
      
      return true;
    });

    setFilteredEntries(filtered);
    toast.success(`Showing ${filtered.length} entries`);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const dt = new Date(timestamp);
    return dt.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getSyncStatus = (entry) => {
    if (entry.synced === false || unsyncedEntries.some(e => e.entry_id === entry.entry_id)) {
      return { status: 'pending', label: 'Pending Sync', color: 'text-orange-600', icon: Clock };
    }
    if (entry.synced === true || !entry.created_offline) {
      return { status: 'synced', label: 'Synced', color: 'text-green-600', icon: CheckCircle };
    }
    return { status: 'unknown', label: 'Unknown', color: 'text-gray-600', icon: XCircle };
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">My Inward Entries</h1>
              <p className="text-sm text-[#E8DCC8]">View and verify your submissions</p>
            </div>
          </div>
          <Button onClick={fetchEntries} variant="secondary" className="h-10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">Filter Entries</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleFilter}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white h-10 w-full"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Apply Filter
              </Button>
            </div>
          </div>
          {unsyncedEntries.length > 0 && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <Clock className="w-4 h-4 inline mr-2" />
                {unsyncedEntries.length} entries pending sync to server
              </p>
            </div>
          )}
        </div>

        {/* Entries Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
            Entries ({filteredEntries.length})
          </h2>

          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-12 text-[#5A5F6E]">
              No entries found
            </div>
          ) : (
            <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                      <TableHead className="font-semibold text-[#1A1F2E]">Entry ID</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Document Barcode</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Date & Time</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Bundles</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Images</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Sync Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.map((entry) => {
                      const syncInfo = getSyncStatus(entry);
                      const SyncIcon = syncInfo.icon;
                      
                      return (
                        <TableRow key={entry.entry_id} className="hover:bg-[#FFFBF5]">
                          <TableCell className="font-mono text-sm font-medium text-[#1A1F2E]">
                            {entry.entry_id}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-[#5A5F6E]">
                            {entry.document_barcode}
                          </TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">
                            {formatDateTime(entry.timestamp || entry.created_at)}
                          </TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">
                            {entry.bundles?.length || 0}
                          </TableCell>
                          <TableCell className="text-sm text-[#5A5F6E]">
                            {entry.image_urls?.length || 0}
                          </TableCell>
                          <TableCell>
                            <div className={`flex items-center gap-2 ${syncInfo.color}`}>
                              <SyncIcon className="w-4 h-4" />
                              <span className="text-sm font-medium">{syncInfo.label}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MyEntries;